/**
 * Attribution Overrides — admin section on EventDetail.
 *
 * Registration QR Phase 2. Manager/admin/superadmin can:
 *   - See attendees on this event with their effective rep attribution
 *   - Multi-select attendees + bulk-reassign to a target sales_position
 *   - Clear an override (revert to original CTA-derived attribution)
 *   - See append-only audit history with old/new effective rep
 *
 * Gated server-side via the bulk_reassign_event_attendees RPC's role
 * check. The parent component should still hide this section for
 * non-eligible roles to avoid surprise 403s.
 */

import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  bulkReassignAttendees,
  listEventAttributionOverrides,
  type AttributionOverrideRow,
} from "../../services/eventAttribution.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
  type PositionNameRow,
} from "../../services/reportsSuite.service";
import { supabase } from "../../services/supabase";
import { useToast } from "../ui/Toast";
import Button from "../common/Button";
import type { UUID } from "../../types/common.types";
import {
  sortSalesPositionsByFirstName,
  displaySalesPositionName,
} from "../../utils/salesPositionDisplay";

interface AttendeeRow {
  attendee_id: UUID;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  cta_id: UUID | null;
  cta_name: string | null;
  cta_sales_position_id: UUID | null;
  attributed_sales_position_id: UUID | null;
}

interface SalesPositionOption {
  id: UUID;
  title: string;
  assigned_name: string | null;
  is_active: boolean;
}

interface Props {
  eventId: UUID;
}

const AttributionOverridesSection: React.FC<Props> = ({ eventId }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<Set<UUID>>(
    new Set(),
  );
  const [targetPositionId, setTargetPositionId] = useState<string>("");
  const [reason, setReason] = useState("");
  // Should-Fix #4 (2026-06-07 post-deploy audit). Filter chips so
  // an operator can isolate the rows they want to reassign.
  //   all          → everyone
  //   generic      → CTA exists but has no sales_position_id
  //                  (the "Generic — no rep" auto-mint case)
  //   specific_rep → attached to a rep CTA, no manual override yet
  //   overridden   → has attributed_sales_position_id set
  //   unattributed → neither CTA nor override yields a position
  //                  (effective rep is NULL — walk-ins surfaced by
  //                   Must-Fix #1)
  type AttributionFilter =
    | "all"
    | "generic"
    | "specific_rep"
    | "overridden"
    | "unattributed";
  const [filter, setFilter] = useState<AttributionFilter>("all");

  // All event attendees + their current effective attribution.
  const { data: attendees, isLoading: attendeesLoading } = useQuery({
    queryKey: ["event-attendees-attribution", eventId],
    queryFn: async (): Promise<AttendeeRow[]> => {
      const { data, error } = await supabase
        .from("event_attendees")
        .select(
          "id, first_name, last_name, email, cta_id, attributed_sales_position_id, event_call_to_actions(name, sales_position_id)",
        )
        .eq("event_id", eventId);
      if (error) {
        throw error;
      }
      return (data ?? []).map(
        (r: {
          id: string;
          first_name: string | null;
          last_name: string | null;
          email: string | null;
          cta_id: string | null;
          attributed_sales_position_id: string | null;
          event_call_to_actions: {
            name: string | null;
            sales_position_id: string | null;
          } | null;
        }) => ({
          attendee_id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
          email: r.email,
          cta_id: r.cta_id,
          cta_name: r.event_call_to_actions?.name ?? null,
          cta_sales_position_id:
            r.event_call_to_actions?.sales_position_id ?? null,
          attributed_sales_position_id: r.attributed_sales_position_id,
        }),
      );
    },
  });

  // Active sales positions for the target picker.
  const { data: positions } = useQuery({
    queryKey: ["sales-positions-active-for-override"],
    queryFn: async (): Promise<SalesPositionOption[]> => {
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, is_active")
        .eq("is_active", true)
        .order("assigned_name", { ascending: true, nullsFirst: false });
      return (data ?? []) as SalesPositionOption[];
    },
  });

  // Audit log for the recent-activity feed
  const { data: overrides } = useQuery({
    queryKey: ["event-attribution-overrides", eventId],
    queryFn: () => listEventAttributionOverrides(eventId, 50),
  });

  // Hydrate position names referenced in the audit log + effective
  // position display
  const referencedPositionIds = useMemo(() => {
    const ids = new Set<UUID>();
    (attendees ?? []).forEach((a) => {
      if (a.attributed_sales_position_id) {
        ids.add(a.attributed_sales_position_id);
      }
      if (a.cta_sales_position_id) {
        ids.add(a.cta_sales_position_id);
      }
    });
    (overrides ?? []).forEach((o) => {
      [
        o.old_override_position_id,
        o.new_override_position_id,
        o.old_effective_position_id,
        o.new_effective_position_id,
      ].forEach((id) => {
        if (id) {
          ids.add(id);
        }
      });
    });
    return Array.from(ids);
  }, [attendees, overrides]);

  const { data: positionRows } = useQuery({
    queryKey: ["position-names-bulk-override", referencedPositionIds],
    queryFn: () => resolvePositionNamesBulk(referencedPositionIds),
    enabled: referencedPositionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById((positionRows as PositionNameRow[]) ?? []),
    [positionRows],
  );

  const repDisplay = (id: UUID | null): string => {
    if (!id) {
      return "Unattributed";
    }
    const p = positionsById.get(id);
    if (!p) {
      return id;
    }
    return p.assigned_name || p.title;
  };

  const reassignMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedAttendeeIds);
      const target =
        targetPositionId === "" ? null : (targetPositionId as UUID);
      return bulkReassignAttendees(ids, target, reason || null);
    },
    onSuccess: (result) => {
      showToast("success", `Reassigned ${result.length} attendee(s)`);
      setSelectedAttendeeIds(new Set());
      setTargetPositionId("");
      setReason("");
      queryClient.invalidateQueries({
        queryKey: ["event-attendees-attribution", eventId],
      });
      queryClient.invalidateQueries({
        queryKey: ["event-attribution-overrides", eventId],
      });
      // Phase 1.1 / WS4 reports depend on these.
      queryClient.invalidateQueries({ queryKey: ["event-vm-stats", eventId] });
      queryClient.invalidateQueries({ queryKey: ["report-sales-by-rep"] });
      queryClient.invalidateQueries({ queryKey: ["report-verified-members"] });
      queryClient.invalidateQueries({ queryKey: ["msr-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["msr-event-attendees"] });
    },
    onError: (err) => {
      showToast(
        "error",
        err instanceof Error ? err.message : "Reassignment failed",
      );
    },
  });

  const toggleAttendee = (id: UUID) => {
    setSelectedAttendeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (!attendees) {
      return;
    }
    if (selectedAttendeeIds.size === attendees.length) {
      setSelectedAttendeeIds(new Set());
    } else {
      setSelectedAttendeeIds(new Set(attendees.map((a) => a.attendee_id)));
    }
  };

  const filterCounts = useMemo(() => {
    const list = attendees ?? [];
    let generic = 0;
    let specific = 0;
    let overridden = 0;
    let unattributed = 0;
    for (const a of list) {
      const override = a.attributed_sales_position_id;
      if (override) {
        overridden += 1;
        continue;
      }
      // No override: bucket by the CTA's intrinsic state.
      if (a.cta_id === null) {
        unattributed += 1;
      } else if (a.cta_sales_position_id === null) {
        generic += 1;
      } else {
        specific += 1;
      }
    }
    return {
      all: list.length,
      generic,
      specific_rep: specific,
      overridden,
      unattributed,
    };
  }, [attendees]);

  const filteredAttendees = useMemo(() => {
    const list = attendees ?? [];
    if (filter === "all") {
      return list;
    }
    return list.filter((a) => {
      const hasOverride = a.attributed_sales_position_id !== null;
      if (filter === "overridden") {
        return hasOverride;
      }
      if (hasOverride) {
        // overridden rows only surface under the "overridden" chip
        return false;
      }
      if (filter === "generic") {
        return a.cta_id !== null && a.cta_sales_position_id === null;
      }
      if (filter === "specific_rep") {
        return a.cta_id !== null && a.cta_sales_position_id !== null;
      }
      if (filter === "unattributed") {
        return a.cta_id === null;
      }
      return true;
    });
  }, [attendees, filter]);

  // Drop selections that fell out of the filter so the bulk-apply
  // doesn't act on hidden rows.
  React.useEffect(() => {
    setSelectedAttendeeIds((prev) => {
      const visible = new Set(filteredAttendees.map((a) => a.attendee_id));
      const next = new Set<UUID>();
      for (const id of prev) {
        if (visible.has(id)) {
          next.add(id);
        }
      }
      return next;
    });
  }, [filteredAttendees]);

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        Attribution Overrides
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Reassign attendees from one rep to another. Original CTA linkage and
        vouchers are NOT changed; only reports + the rep's portal reflect the
        override. Audit log captures every change.
      </p>

      {/* Should-Fix #4 — filter chips. Counts derived from full
          attendee list so the operator sees the breakdown even
          when a filter is active. */}
      {(attendees?.length ?? 0) > 0 && (
        <div
          className="flex flex-wrap gap-2 mb-3"
          role="tablist"
          aria-label="Filter attendees"
        >
          {(
            [
              { key: "all", label: "All" },
              { key: "generic", label: "Generic CTA" },
              { key: "specific_rep", label: "Specific rep" },
              { key: "overridden", label: "Overridden" },
              { key: "unattributed", label: "Unattributed" },
            ] as const
          ).map((chip) => {
            const count = filterCounts[chip.key];
            const isActive = filter === chip.key;
            const isDisabled = count === 0 && chip.key !== "all";
            return (
              <button
                key={chip.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                disabled={isDisabled}
                onClick={() => setFilter(chip.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                  isActive
                    ? "bg-blue-600 text-white border-blue-600"
                    : isDisabled
                      ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {chip.label}{" "}
                <span className={isActive ? "opacity-90" : "text-gray-500"}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {attendeesLoading && (
        <p className="text-sm text-gray-500">Loading attendees…</p>
      )}

      {filteredAttendees.length === 0 && !attendeesLoading && (
        <p className="text-sm text-gray-500">
          {(attendees?.length ?? 0) === 0
            ? "No attendees yet. Filter chips will appear here once attendees register."
            : "No attendees match this filter."}
        </p>
      )}

      {filteredAttendees.length > 0 && (
        <>
          <div className="overflow-x-auto rounded border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={
                        selectedAttendeeIds.size === filteredAttendees.length &&
                        filteredAttendees.length > 0
                      }
                      onChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-2">Attendee</th>
                  <th className="px-3 py-2">Original CTA</th>
                  <th className="px-3 py-2">Effective Rep</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAttendees.map((a) => {
                  const effective =
                    a.attributed_sales_position_id ?? a.cta_sales_position_id;
                  return (
                    <tr key={a.attendee_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedAttendeeIds.has(a.attendee_id)}
                          onChange={() => toggleAttendee(a.attendee_id)}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {[a.first_name, a.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                          a.email ||
                          "(unknown)"}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {a.cta_name ?? "(no CTA)"}
                      </td>
                      <td className="px-3 py-2">
                        {repDisplay(effective)}
                        {a.attributed_sales_position_id && (
                          <span className="ml-2 text-xs text-amber-700">
                            (overridden)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Target rep
              </label>
              <select
                value={targetPositionId}
                onChange={(e) => setTargetPositionId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">— Clear override (revert) —</option>
                {sortSalesPositionsByFirstName(positions ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {displaySalesPositionName(p)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., rep substitution day-of"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => reassignMutation.mutate()}
              disabled={
                selectedAttendeeIds.size === 0 || reassignMutation.isPending
              }
            >
              {reassignMutation.isPending
                ? "Reassigning…"
                : `Reassign ${selectedAttendeeIds.size}`}
            </Button>
          </div>
        </>
      )}

      {(overrides ?? []).length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Recent reassignments
          </h4>
          <ul className="space-y-1 text-xs text-gray-700 max-h-60 overflow-y-auto">
            {(overrides as AttributionOverrideRow[]).map((o) => (
              <li
                key={o.id}
                className="bg-gray-50 border border-gray-200 rounded px-2 py-1"
              >
                <span className="font-medium">
                  {repDisplay(o.old_effective_position_id)}
                </span>
                {" → "}
                <span className="font-medium">
                  {repDisplay(o.new_effective_position_id)}
                </span>
                {o.reason && (
                  <span className="text-gray-500"> · {o.reason}</span>
                )}
                <span className="text-gray-400">
                  {" "}
                  · {new Date(o.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AttributionOverridesSection;
